import { formatLocaleDate, formatLocaleNumber } from "@/utils/formatUtils";
import { licensePlateNormalizeFunction } from "@/utils/formUtils";
import { isDefined, removeStringWhiteSpaces } from "@/utils/utils";
import { Alert, Descriptions } from "antd";
import { HookAPI } from "antd/es/modal/useModal";
import { useTranslation } from "react-i18next";
import t from "../../../../app/i18n";
import { Vehicle } from "../../../contract/types";
import { AutocompleteVehiclesBy } from "../enums";

interface ContentProps {
  vehicle: Vehicle;
  licensePlate?: string;
  focusedItem?: AutocompleteVehiclesBy;
}

const VehicleFoundModalContent = ({ vehicle, licensePlate, focusedItem }: ContentProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Descriptions
        bordered
        size="small"
        className="margin-right-medium"
        column={1}
        styles={{ label: { width: 160 } }}
        items={[
          {
            label: t("common.vehicle"),
            children: `${vehicle.customBrand || vehicle.model?.brand?.name || ""} ${
              vehicle.customModel || vehicle.model?.name || ""
            }`,
          },
          {
            label: t("contract.attrs.insurances.licensePlate"),
            children: licensePlateNormalizeFunction(vehicle.licensePlate),
          },
          {
            label: t("contract.attrs.insurances.vin"),
            children: vehicle.vin,
          },
          {
            label: t("calc.vehicle.sections.otherData"),
            children: [
              vehicle.category ? t("contract.enums.vehicleCategory." + vehicle.category) : undefined,
              formatLocaleDate(vehicle.firstRegistrationDate),
              isDefined(vehicle.engineDisplacement)
                ? `${formatLocaleNumber(vehicle.engineDisplacement)} ${t("common.sign.engineDisplacement")}`
                : undefined,
              isDefined(vehicle.enginePower)
                ? `${formatLocaleNumber(vehicle.enginePower)} ${t("common.sign.enginePower")}`
                : undefined,
              isDefined(vehicle.electricEnginePower)
                ? `${formatLocaleNumber(vehicle.electricEnginePower)} ${t("common.sign.enginePower")}`
                : undefined,
              vehicle.fuelType ? t("contract.enums.fuelType." + vehicle.fuelType) : undefined,
              isDefined(vehicle.totalWeight)
                ? `${formatLocaleNumber(vehicle.totalWeight)} ${t("common.sign.kg")}`
                : undefined,
            ]
              .filter((v) => !!v)
              .join(", "),
          },
        ]}
      />

      {focusedItem === AutocompleteVehiclesBy.VIN &&
      licensePlate &&
      removeStringWhiteSpaces(licensePlate) !== vehicle.licensePlate ? (
        <Alert
          className="margin-top-small margin-right-medium"
          type="warning"
          showIcon
          title={t("calc.vehicle.helpers.twoPlatesFound", { licensePlate })}
        />
      ) : (
        <Alert
          className="margin-top-small margin-right-medium"
          type="info"
          showIcon
          title={t("calc.vehicle.helpers.vehicleFillCheck")}
        />
      )}
    </>
  );
};

interface Props extends ContentProps {
  modal: HookAPI;
  onConfirm: () => void;
}

export const showVehicleFoundModal = ({ modal, vehicle, licensePlate, focusedItem, onConfirm }: Props) =>
  modal.confirm({
    title: t("calc.vehicle.helpers.vehicleFound"),
    content: <VehicleFoundModalContent vehicle={vehicle} licensePlate={licensePlate} focusedItem={focusedItem} />,
    width: 630,
    okText: t("common.yes"),
    cancelText: t("common.no"),
    onOk: onConfirm,
  });
